import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Separator } from './ui/separator';
import { Checkbox } from './ui/checkbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Turkish translations
const TR = {
  adminLogin: 'Yönetici Girişi',
  signInAccess: 'Yönetim paneline erişmek için giriş yapın',
  username: 'Kullanıcı Adı',
  password: 'Şifre',
  signIn: 'Giriş Yap',
  logout: 'Çıkış Yap',
  dashboard: 'Kontrol Paneli',
  videos: 'Videolar',
  categories: 'Kategoriler',
  ads: 'Reklamlar',
  bulkImport: 'Toplu İçe Aktarım',
  contacts: 'İletişim Mesajları',
  help: 'Yardım Talepleri',
  reports: 'İçerik Şikayetleri',
  totalVideos: 'Toplam Video',
  totalCategories: 'Toplam Kategori',
  activeAds: 'Aktif Reklamlar',
  totalContacts: 'İletişim Mesajları',
  adPerformance: 'Reklam Performansı',
  totalImpressions: 'Toplam Gösterim',
  totalClicks: 'Toplam Tıklama',
  recentActivity: 'Son Aktivite',
  videoManagement: 'Video Yönetimi',
  addNewVideo: 'Yeni Video Ekle',
  createNewVideo: 'Yeni Video Oluştur',
  title: 'Başlık',
  embedUrl: 'Embed URL',
  originalSourceUrl: 'Orijinal Kaynak URL',
  description: 'Açıklama',
  thumbnailUrl: 'Küçük Resim URL',
  duration: 'Süre (saniye)',
  tags: 'Etiketler (virgülle ayırın)',
  featuredVideo: 'Öne Çıkan Video',
  latestUpload: 'Son Yükleme',
  createVideo: 'Video Oluştur',
  delete: 'Sil',
  views: 'görüntüleme',
  featured: 'Öne Çıkan',
  categoryManagement: 'Kategori Yönetimi',
  addNewCategory: 'Yeni Kategori Ekle',
  createNewCategory: 'Yeni Kategori Oluştur',
  name: 'Ad',
  imageUrl: 'Resim URL',
  createCategory: 'Kategori Oluştur',
  videosInCategory: 'video',
  adManagement: 'Reklam Yönetimi',
  addNewAd: 'Yeni Reklam Ekle',
  createNewAd: 'Yeni Reklam Oluştur',
  adName: 'Reklam Adı',
  adType: 'Reklam Türü',
  bannerAd: 'Banner Reklamı',
  prerollVideoAd: 'Ön Rulo Video Reklamı',
  midrollVideoAd: 'Orta Rulo Video Reklamı',
  postrollVideoAd: 'Son Rulo Video Reklamı',
  bannerUrl: 'Banner URL',
  clickUrl: 'Tıklama URL',
  width: 'Genişlik',
  height: 'Yükseklik',
  priority: 'Öncelik',
  createAd: 'Reklam Oluştur',
  impressions: 'gösterim',
  clicks: 'tıklama',
  bulkVideoImport: 'Toplu Video İçe Aktarımı',
  bulkImportDescription: 'Birden fazla videoyu aynı anda içe aktarın. Video URL\'lerini girin (her satırda bir tane)',
  importVideos: 'Videoları İçe Aktar',
  contactMessages: 'İletişim Mesajları',
  sender: 'Gönderen',
  subject: 'Konu',
  message: 'Mesaj',
  noMessages: 'Henüz mesaj yok',
  managementPanel: 'Yönetim Paneli'
};

const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('adminToken'));
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [dashboard, setDashboard] = useState({});
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ads, setAds] = useState([]);
  const [nativeAds, setNativeAds] = useState([]);
  const [adPlacements, setAdPlacements] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [helpRequests, setHelpRequests] = useState([]);
  const [contentReports, setContentReports] = useState([]);
  const [analytics, setAnalytics] = useState({});
  
  // Form states
  const [newVideo, setNewVideo] = useState({
    title: '',
    description: '',
    embed_url: '',
    original_source_url: '',
    thumbnail_url: '',
    duration: '',
    category_ids: [],
    tags: '',
    is_featured: false,
    is_latest_upload: false
  });
  
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
    image_url: ''
  });
  
  const [newAd, setNewAd] = useState({
    name: '',
    ad_type: 'banner',
    banner_url: '',
    iframe_code: '',
    vast_xml: '',
    click_url: '',
    width: '',
    height: '',
    priority: 1
  });

  const [newNativeAd, setNewNativeAd] = useState({
    title: '',
    description: '',
    image_url: '',
    click_url: '',
    placement: 'bottom_page',
    priority: 1
  });
  // Ad edit states
  const [editingAd, setEditingAd] = useState(null);
  const [showEditAdDialog, setShowEditAdDialog] = useState(false);
  const [editAdForm, setEditAdForm] = useState({
    name: '',
    ad_type: 'banner',
    banner_url: '',
    iframe_code: '',
    vast_xml: '',
    click_url: '',
    width: '',
    height: '',
    priority: 1
  });
  
  // Placement ad management states
  const [showPlacementDialog, setShowPlacementDialog] = useState(false);
  const [editingPlacement, setEditingPlacement] = useState(null);
  const [placementAdForm, setPlacementAdForm] = useState({
    iframe_code: '',
    vast_url: '',
    native_code: '',
    is_active: true
  });
  const [placementPreview, setPlacementPreview] = useState('');

  const [bulkVideos, setBulkVideos] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [selectedCategoryForScraping, setSelectedCategoryForScraping] = useState('');
  const [maxVideosToScrape, setMaxVideosToScrape] = useState(0);
  const [scrapingInProgress, setScrapingInProgress] = useState(false);
  
  // File upload states
  const [uploadingCategoryImage, setUploadingCategoryImage] = useState(false);
  const [extractingVideoMetadata, setExtractingVideoMetadata] = useState(false);
  
  // Category assignment states
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  
  // Category video management states
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [categoryVideos, setCategoryVideos] = useState([]);
  const [categoryVideoCount, setCategoryVideoCount] = useState({});
  const [showCategoryVideos, setShowCategoryVideos] = useState(false);
  
  // Bulk operations states
  const [selectedVideoIds, setSelectedVideoIds] = useState([]);
  const [selectedCategoryVideoIds, setSelectedCategoryVideoIds] = useState([]);
  const [bulkDeleteInProgress, setBulkDeleteInProgress] = useState(false);
  
  // Category edit states
  const [editingCategory, setEditingCategory] = useState(null);
  const [showEditCategoryDialog, setShowEditCategoryDialog] = useState(false);
  const [editCategoryForm, setEditCategoryForm] = useState({
    name: '',
    description: '',
    image_url: '',
    image_file: null
  });
  
  // Bulk category operations
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [bulkCategoryDeleteInProgress, setBulkCategoryDeleteInProgress] = useState(false);

  useEffect(() => {
    if (token) {
      setIsAuthenticated(true);
      fetchDashboard();
      fetchVideos();
      fetchCategories();
      fetchAds();
      fetchAdPlacements();
      fetchNativeAds();
      fetchContactMessages();
      fetchHelpRequests();
      fetchContentReports();
      fetchAnalytics();
    }
  }, [token]);

  const axiosConfig = {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };

  // Add axios interceptor for token validation
  React.useEffect(() => {
    if (token) {
      const interceptor = axios.interceptors.response.use(
        (response) => response,
        (error) => {
          if (error.response?.status === 401) {
            console.error('Token invalid, logging out...');
            handleLogout();
          }
          return Promise.reject(error);
        }
      );
      return () => axios.interceptors.response.eject(interceptor);
    }
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/login`, loginData);
      const { access_token } = response.data;
      setToken(access_token);
      localStorage.setItem('adminToken', access_token);
      setIsAuthenticated(true);
    } catch (error) {
      alert('Login failed: ' + error.response?.data?.detail || error.message);
    }
  };

  const handleLogout = () => {
    setToken(null);
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
  };

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard`, axiosConfig);
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    }
  };

  const fetchVideos = async () => {
    try {
      console.log('Fetching videos with auth token:', token ? 'Present' : 'Missing');
      const response = await axios.get(`${API}/videos?limit=1000`, axiosConfig);
      console.log('Videos API response:', response.data);
      
      // Handle paginated response structure
      const videosData = response.data;
      if (Array.isArray(videosData)) {
        console.log(`Found ${videosData.length} videos (direct array)`);
        setVideos(videosData);
      } else if (videosData && Array.isArray(videosData.items)) {
        console.log(`Found ${videosData.items.length} videos (items array)`);
        setVideos(videosData.items);
      } else if (videosData && Array.isArray(videosData.videos)) {
        console.log(`Found ${videosData.videos.length} videos (videos array)`);
        setVideos(videosData.videos);
      } else {
        console.warn('Videos API returned unexpected format:', videosData);
        console.log('Full response structure:', Object.keys(videosData || {}));
        setVideos([]);
      }
    } catch (error) {
      console.error('Failed to fetch videos:', error);
      console.error('Error details:', error.response?.data);
      setVideos([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, axiosConfig);
      const categoriesData = response.data;
      if (Array.isArray(categoriesData)) {
        setCategories(categoriesData);
        await fetchCategoryVideoCounts(categoriesData);
      } else {
        console.warn('Categories API returned unexpected format:', categoriesData);
        setCategories([]);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      setCategories([]);
    }
  };

  const fetchCategoryVideoCounts = async (categoriesData) => {
    try {
      const counts = {};
      for (const category of categoriesData) {
        const response = await axios.get(`${API}/videos?category_id=${category.id}&limit=1`, axiosConfig);
        counts[category.id] = response.data.total || 0;
      }
      setCategoryVideoCount(counts);
    } catch (error) {
      console.error('Failed to fetch category video counts:', error);
    }
  };

  const fetchCategoryVideos = async (categoryId) => {
    try {
      const response = await axios.get(`${API}/videos?category_id=${categoryId}&limit=100`, axiosConfig);
      setCategoryVideos(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch category videos:', error);
      setCategoryVideos([]);
    }
  };

  const handleCategoryClick = async (categoryId) => {
    setSelectedCategoryId(categoryId);
    setShowCategoryVideos(true);
    await fetchCategoryVideos(categoryId);
  };

  const handleDeleteVideo = async (videoId, videoTitle = null) => {
    const confirmMessage = videoTitle 
      ? `Are you sure you want to delete "${videoTitle}"? This action cannot be undone.`
      : 'Are you sure you want to delete this video?';
    
    if (window.confirm(confirmMessage)) {
      try {
        await axios.delete(`${API}/videos/${videoId}`, axiosConfig);
        // Refresh the category videos list if we have a selected category
        if (selectedCategoryId) {
          await fetchCategoryVideos(selectedCategoryId);
        }
        // Refresh main videos list and counts
        fetchVideos();
        fetchCategories();
        fetchDashboard();
        alert('Video deleted successfully!');
      } catch (error) {
        alert('Failed to delete video: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleBulkDeleteVideos = async (videoIds, isFromCategory = false) => {
    if (videoIds.length === 0) {
      alert('Please select videos to delete');
      return;
    }

    if (window.confirm(`Are you sure you want to delete ${videoIds.length} selected videos?`)) {
      setBulkDeleteInProgress(true);
      try {
        let successCount = 0;
        let errorCount = 0;

        for (const videoId of videoIds) {
          try {
            await axios.delete(`${API}/videos/${videoId}`, axiosConfig);
            successCount++;
          } catch (error) {
            console.error(`Failed to delete video ${videoId}:`, error);
            errorCount++;
          }
        }

        // Clear selections
        if (isFromCategory) {
          setSelectedCategoryVideoIds([]);
          await fetchCategoryVideos(selectedCategoryId);
        } else {
          setSelectedVideoIds([]);
        }
        
        // Refresh data
        fetchVideos();
        fetchCategories();

        alert(`Bulk deletion completed!\nSuccessfully deleted: ${successCount}\nFailed: ${errorCount}`);
      } catch (error) {
        alert('Bulk deletion failed: ' + (error.response?.data?.detail || error.message));
      } finally {
        setBulkDeleteInProgress(false);
      }
    }
  };

  const handleVideoSelection = (videoId, isFromCategory = false) => {
    if (isFromCategory) {
      setSelectedCategoryVideoIds(prev => 
        prev.includes(videoId) 
          ? prev.filter(id => id !== videoId)
          : [...prev, videoId]
      );
    } else {
      setSelectedVideoIds(prev => 
        prev.includes(videoId) 
          ? prev.filter(id => id !== videoId)
          : [...prev, videoId]
      );
    }
  };

  const handleSelectAllVideos = (videos, isFromCategory = false) => {
    const allVideoIds = videos.map(video => video.id);
    if (isFromCategory) {
      setSelectedCategoryVideoIds(
        selectedCategoryVideoIds.length === allVideoIds.length ? [] : allVideoIds
      );
    } else {
      setSelectedVideoIds(
        selectedVideoIds.length === allVideoIds.length ? [] : allVideoIds
      );
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds <= 0) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
  };

  const isProtectedCategory = (categoryName) => {
    const protectedNames = [
      'featured videos', 
      'featured',
      'latest releases',
      'latest videos'
    ];
    return protectedNames.some(protectedName => 
      categoryName.toLowerCase().includes(protectedName) || 
      protectedName.includes(categoryName.toLowerCase())
    );
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setEditCategoryForm({
      name: category.name,
      description: category.description || '',
      image_url: category.image_url || ''
    });
    setShowEditCategoryDialog(true);
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    if (!editingCategory) return;

    try {
      let imageUrl = editCategoryForm.image_url;

      // Handle file upload if a file was selected
      if (editCategoryForm.image_file) {
        // Create FormData directly without additional processing
        const formData = new FormData();
        formData.append('image', editCategoryForm.image_file);
        
        console.log('Uploading image file:', editCategoryForm.image_file.name, editCategoryForm.image_file.type);
        
        // Upload the image first
        const uploadResponse = await axios.post(`${API}/upload-category-image`, formData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        console.log('Upload response:', uploadResponse.data);
        
        if (uploadResponse.data && uploadResponse.data.image_url) {
          imageUrl = uploadResponse.data.image_url;
          console.log('New image URL:', imageUrl);
        } else {
          throw new Error('No image URL returned from upload');
        }
      }

      const updateData = {
        name: editCategoryForm.name,
        description: editCategoryForm.description,
        image_url: imageUrl
      };

      console.log('Updating category with data:', updateData);

      await axios.put(`${API}/categories/${editingCategory.id}`, updateData, axiosConfig);
      
      setShowEditCategoryDialog(false);
      setEditingCategory(null);
      setEditCategoryForm({ name: '', description: '', image_url: '', image_file: null });
      
      // Refresh categories
      await fetchCategories();
      
      alert('Category updated successfully with image!');
    } catch (error) {
      console.error('Category update error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Failed to update category: ${errorMessage}`);
    }
  };

  const handleDeleteCategory = async (category) => {
    if (isProtectedCategory(category.name)) {
      alert('This category cannot be deleted as it is protected (Featured Videos/Latest Videos)');
      return;
    }

    if (window.confirm(`Are you sure you want to delete the category "${category.name}"? This will remove the category but not the videos assigned to it.`)) {
      try {
        await axios.delete(`${API}/categories/${category.id}`, axiosConfig);
        
        // Refresh categories
        await fetchCategories();
        
        alert('Category deleted successfully!');
      } catch (error) {
        const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
        alert(`Failed to delete category: ${errorMessage}`);
      }
    }
  };

  const handleBulkDeleteCategories = async () => {
    const nonProtectedIds = selectedCategoryIds.filter(id => {
      const category = categories.find(c => c.id === id);
      return category && !isProtectedCategory(category.name);
    });

    if (nonProtectedIds.length === 0) {
      alert('No deletable categories selected (protected categories cannot be deleted)');
      return;
    }

    if (window.confirm(`Are you sure you want to delete ${nonProtectedIds.length} selected categories?`)) {
      setBulkCategoryDeleteInProgress(true);
      try {
        let successCount = 0;
        let errorCount = 0;

        for (const categoryId of nonProtectedIds) {
          try {
            await axios.delete(`${API}/categories/${categoryId}`, axiosConfig);
            successCount++;
          } catch (error) {
            console.error(`Failed to delete category ${categoryId}:`, error);
            errorCount++;
          }
        }

        // Clear selections and refresh
        setSelectedCategoryIds([]);
        await fetchCategories();

        alert(`Bulk deletion completed!\nSuccessfully deleted: ${successCount}\nFailed: ${errorCount}`);
      } catch (error) {
        alert('Bulk deletion failed: ' + (error.response?.data?.detail || error.message));
      } finally {
        setBulkCategoryDeleteInProgress(false);
      }
    }
  };

  const handleCategorySelection = (categoryId) => {
    setSelectedCategoryIds(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleSelectAllCategories = () => {
    const selectableCategories = categories.filter(cat => !isProtectedCategory(cat.name));
    const allSelectableIds = selectableCategories.map(cat => cat.id);
    
    if (selectedCategoryIds.length === allSelectableIds.length) {
      setSelectedCategoryIds([]);
    } else {
      setSelectedCategoryIds(allSelectableIds);
    }
  };

  const validateImageFile = (file) => {
    // Validate file type (JPG and PNG only)
    if (!file.type.match(/^image\/(jpeg|jpg|png)$/i)) {
      alert('Please select a JPG or PNG image file');
      return false;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image file must be less than 5MB');
      return false;
    }

    return true;
  };

  const sortedCategories = categories.sort((a, b) => {
    // Featured Videos always at top
    const aIsProtected = isProtectedCategory(a.name);
    const bIsProtected = isProtectedCategory(b.name);
    
    if (aIsProtected && !bIsProtected) return -1;
    if (!aIsProtected && bIsProtected) return 1;
    
    // Within protected or non-protected, sort alphabetically
    return a.name.localeCompare(b.name);
  });

  const fetchAds = async () => {
    try {
      const response = await axios.get(`${API}/ads`, axiosConfig);
      const adsData = response.data;
      if (Array.isArray(adsData)) {
        setAds(adsData);
      } else {
        console.warn('Ads API returned unexpected format:', adsData);
        setAds([]);
      }
    } catch (error) {
      console.error('Failed to fetch ads:', error);
      setAds([]);
    }
  };

  const fetchAdPlacements = async () => {
    try {
      const response = await axios.get(`${API}/ad-placements`, axiosConfig);
      setAdPlacements(response.data);
    } catch (error) {
      console.error('Error fetching ad placements:', error);
    }
  };

  const fetchNativeAds = async () => {
    try {
      const response = await axios.get(`${API}/native-ads`, axiosConfig);
      const nativeAdsData = response.data;
      if (Array.isArray(nativeAdsData)) {
        setNativeAds(nativeAdsData);
      } else {
        console.warn('Native ads API returned unexpected format:', nativeAdsData);
        setNativeAds([]);
      }
    } catch (error) {
      console.error('Failed to fetch native ads:', error);
      setNativeAds([]);
    }
  };

  const fetchContactMessages = async () => {
    try {
      const response = await axios.get(`${API}/admin/contact-submissions`, axiosConfig);
      const messagesData = response.data;
      if (Array.isArray(messagesData)) {
        setContactMessages(messagesData);
      } else {
        console.warn('Contact messages API returned unexpected format:', messagesData);
        setContactMessages([]);
      }
    } catch (error) {
      console.error('Failed to fetch contact messages:', error);
      setContactMessages([]);
    }
  };

  const fetchHelpRequests = async () => {
    try {
      const response = await axios.get(`${API}/admin/help-requests`, axiosConfig);
      const helpData = response.data;
      if (Array.isArray(helpData)) {
        setHelpRequests(helpData);
      } else {
        console.warn('Help requests API returned unexpected format:', helpData);
        setHelpRequests([]);
      }
    } catch (error) {
      console.error('Failed to fetch help requests:', error);
      setHelpRequests([]);
    }
  };

  const fetchContentReports = async () => {
    try {
      const response = await axios.get(`${API}/admin/content-reports`, axiosConfig);
      const reportsData = response.data;
      if (Array.isArray(reportsData)) {
        setContentReports(reportsData);
      } else {
        console.warn('Content reports API returned unexpected format:', reportsData);
        setContentReports([]);
      }
    } catch (error) {
      console.error('Failed to fetch content reports:', error);
      setContentReports([]);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics`, axiosConfig);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      setAnalytics({});
    }
  };

  const handleCreateVideo = async (e) => {
    e.preventDefault();
    try {
      const videoData = {
        ...newVideo,
        duration: newVideo.duration ? parseInt(newVideo.duration) : null,
        tags: newVideo.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
      };
      
      await axios.post(`${API}/videos`, videoData, axiosConfig);
      alert('Video created successfully!');
      setNewVideo({
        title: '',
        description: '',
        embed_url: '',
        thumbnail_url: '',
        duration: '',
        category_ids: [],
        tags: '',
        is_featured: false
      });
      fetchVideos();
      fetchDashboard();
    } catch (error) {
      alert('Failed to create video: ' + error.response?.data?.detail || error.message);
    }
  };

  const handleCategoryImageUpload = async (file) => {
    if (!file) return null;
    
    setUploadingCategoryImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await axios.post(`${API}/upload-category-image`, formData, {
        ...axiosConfig,
        headers: {
          ...axiosConfig.headers,
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data.image_url;
    } catch (error) {
      alert('Failed to upload image: ' + error.response?.data?.detail || error.message);
      return null;
    } finally {
      setUploadingCategoryImage(false);
    }
  };

  const handleExtractVideoMetadata = async (url) => {
    if (!url) return;
    
    setExtractingVideoMetadata(true);
    try {
      const response = await axios.post(`${API}/videos/extract-metadata`, { url }, axiosConfig);
      const metadata = response.data;
      
      // Auto-assign category if detected
      let categoryIds = [];
      if (metadata.category) {
        const matchingCategory = categories.find(cat => 
          cat.name.toLowerCase().includes(metadata.category.toLowerCase())
        );
        if (matchingCategory) {
          categoryIds = [matchingCategory.id];
        }
      }
      
      setNewVideo(prev => ({
        ...prev,
        title: metadata.title || prev.title,
        description: metadata.description || prev.description,
        thumbnail_url: metadata.thumbnail_url || prev.thumbnail_url,
        duration: metadata.duration || prev.duration,
        tags: Array.isArray(metadata.tags) ? metadata.tags.join(', ') : (metadata.tags || prev.tags),
        category_ids: categoryIds.length > 0 ? categoryIds : prev.category_ids
      }));
      
      alert(`Metadata extracted successfully! ${categoryIds.length > 0 ? 'Category auto-assigned.' : ''}`);
    } catch (error) {
      alert('Failed to extract metadata: ' + error.response?.data?.detail || error.message);
    } finally {
      setExtractingVideoMetadata(false);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/categories`, newCategory, axiosConfig);
      alert('Category created successfully!');
      setNewCategory({ name: '', description: '', image_url: '' });
      fetchCategories();
      fetchDashboard();
    } catch (error) {
      alert('Failed to create category: ' + error.response?.data?.detail || error.message);
    }
  };

  const handleCreateAd = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post(`${API}/ads`, newAd, axiosConfig);
      
      alert('Ad created successfully!');
      setNewAd({ name: '', ad_type: 'banner', banner_url: '', iframe_code: '', vast_xml: '', click_url: '', width: '', height: '', priority: 1 });
      fetchAds();
    } catch (error) {
      console.error('Create ad error:', error);
      let errorMessage = 'Failed to create ad: ';
      
      if (error.response?.status === 401) {
        errorMessage += 'Authentication failed. Please login again.';
        handleLogout();
      } else if (error.response?.data?.detail) {
        errorMessage += error.response.data.detail;
      } else {
        errorMessage += error.message || 'Unknown error';
      }
      
      alert(errorMessage);
    }
  };

  // Placement Ad Management Functions
  const handleEditPlacement = (placement) => {
    setEditingPlacement(placement);
    console.log('Editing placement:', placement);
    console.log('Current ad data:', placement.current_ad);
    
    setPlacementAdForm({
      iframe_code: placement.current_ad?.iframe_code || '',
      vast_url: placement.current_ad?.vast_url || '',
      native_code: placement.current_ad?.native_code || '',
      is_active: placement.current_ad?.is_active !== false
    });
    setPlacementPreview('');
    setShowPlacementDialog(true);
  };

  const handleUpdatePlacement = async (e) => {
    e.preventDefault();
    
    if (!editingPlacement) return;
    
    try {
      // Prepare form data based on ad type
      let formData = { is_active: placementAdForm.is_active };
      
      if (editingPlacement.ad_types?.includes('vast')) {
        if (!placementAdForm.vast_url) {
          alert('VAST URL is required for VAST ads');
          return;
        }
        formData.vast_url = placementAdForm.vast_url.trim();
      } else if (editingPlacement.ad_types?.includes('native')) {
        if (!placementAdForm.native_code) {
          alert('Native ad script is required for native ads');
          return;
        }
        formData.native_code = placementAdForm.native_code;
      } else {
        // Banner ad
        if (!placementAdForm.iframe_code) {
          alert('Iframe code is required for banner ads');
          return;
        }
        formData.iframe_code = placementAdForm.iframe_code;
      }
      
      console.log('Updating placement:', editingPlacement.id, formData);
      
      const response = await axios.put(`${API}/ad-placements/${editingPlacement.id}`, formData, axiosConfig);
      
      console.log('Update successful:', response.data);
      alert('Placement ad updated successfully!');
      setShowPlacementDialog(false);
      setEditingPlacement(null);
      setPlacementAdForm({
        iframe_code: '',
        vast_url: '',
        native_code: '',
        is_active: true
      });
      fetchAdPlacements();
    } catch (error) {
      console.error('Update placement error:', error);
      let errorMessage = 'Failed to update placement ad: ';
      
      if (error.response?.status === 401) {
        errorMessage += 'Authentication failed. Please login again.';
        handleLogout();
      } else if (error.response?.data?.detail) {
        errorMessage += error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else {
        errorMessage += error.message || 'Unknown error';
      }
      
      alert(errorMessage);
    }
  };

  const handlePreviewPlacement = async (placementId) => {
    try {
      const response = await axios.get(`${API}/ad-placements/${placementId}/preview`, axiosConfig);
      setPlacementPreview(response.data.html_content);
    } catch (error) {
      console.error('Preview placement error:', error);
      setPlacementPreview('<div style="text-align: center; color: #666;">Preview not available</div>');
    }
  };

  const handleEditAd = (ad) => {
    // Set the ad data for editing
    setEditingAd(ad);
    setEditAdForm({
      name: ad.name,
      ad_type: ad.ad_type,
      banner_url: ad.banner_url || '',
      iframe_code: ad.iframe_code || '',
      vast_xml: ad.vast_xml || '',
      click_url: ad.click_url || '',
      width: ad.width || '',
      height: ad.height || '',
      priority: ad.priority
    });
    setShowEditAdDialog(true);
  };

  const handleUpdateAd = async (e) => {
    e.preventDefault();
    
    if (!editingAd) return;
    
    try {
      await axios.put(`${API}/ads/${editingAd.id}`, editAdForm, axiosConfig);
      
      alert('Ad updated successfully!');
      setShowEditAdDialog(false);
      setEditingAd(null);
      fetchAds();
    } catch (error) {
      console.error('Update ad error:', error);
      let errorMessage = 'Failed to update ad: ';
      
      if (error.response?.status === 401) {
        errorMessage += 'Authentication failed. Please login again.';
        handleLogout();
      } else if (error.response?.data?.detail) {
        errorMessage += error.response.data.detail;
      } else {
        errorMessage += error.message || 'Unknown error';
      }
      
      alert(errorMessage);
    }
  };

  const handleDeleteAd = async (adId) => {
    if (!window.confirm('Are you sure you want to delete this ad?')) return;
    
    try {
      await axios.delete(`${API}/ads/${adId}`, axiosConfig);
      
      alert('Ad deleted successfully!');
      fetchAds();
    } catch (error) {
      console.error('Delete ad error:', error);
      let errorMessage = 'Failed to delete ad: ';
      
      if (error.response?.status === 401) {
        errorMessage += 'Authentication failed. Please login again.';
        handleLogout();
      } else if (error.response?.data?.detail) {
        errorMessage += error.response.data.detail;
      } else {
        errorMessage += error.message || 'Unknown error';
      }
      
      alert(errorMessage);
    }
  };

  const handleWebsiteScraping = async () => {
    if (!websiteUrl || !selectedCategoryForScraping) {
      alert('Please enter website URL and select a category');
      return;
    }

    setScrapingInProgress(true);
    try {
      const response = await axios.post(`${API}/videos/scrape-website`, {
        website_url: websiteUrl,
        category_id: selectedCategoryForScraping,
        max_videos: maxVideosToScrape
      }, axiosConfig);
      
      const results = response.data;
      const successCount = results.success.length;
      const failedCount = results.failed.length;
      
      let message = `Website scraping completed!\n`;
      message += `🔍 Scraped from: ${websiteUrl}\n`;
      message += `✅ Successfully imported: ${successCount} videos\n`;
      if (failedCount > 0) {
        message += `❌ Failed: ${failedCount} videos\n`;
      }
      
      const categoryName = categories.find(cat => cat.id === selectedCategoryForScraping)?.name || 'Selected';
      message += `\n📂 All videos added to: ${categoryName} category`;
      
      alert(message);
      setWebsiteUrl('');
      setSelectedCategoryForScraping('');
      fetchVideos();
      fetchDashboard();
    } catch (error) {
      console.error('Website scraping error:', error);
      let errorMessage = 'Failed to scrape website: ';
      
      if (error.response?.status === 401) {
        errorMessage += 'Authentication failed. Please login again.';
        handleLogout();
      } else if (error.response?.data?.detail) {
        errorMessage += error.response.data.detail;
      } else {
        errorMessage += error.message || 'Unknown error';
      }
      
      alert(errorMessage);
    } finally {
      setScrapingInProgress(false);
    }
  };

  const handleBulkImport = async () => {
    const urls = bulkVideos.split('\n').filter(url => url.trim());
    if (urls.length === 0) {
      alert('Please enter video URLs (one per line)');
      return;
    }

    // Show processing message
    const processingAlert = `Processing ${urls.length} video URLs...\nThis may take a few minutes.`;
    alert(processingAlert);

    try {
      console.log('Starting bulk import with', urls.length, 'URLs');
      const response = await axios.post(`${API}/videos/bulk-import`, { urls }, {
        ...axiosConfig,
        timeout: 300000 // 5 minutes timeout for bulk operations
      });
      console.log('Bulk import response:', response.data);
      
      const results = response.data;
      
      const successCount = results.success.length;
      const failedCount = results.failed.length;
      const totalCount = results.total;
      
      let message = `🎯 BULK IMPORT COMPLETED!\n\n`;
      message += `📊 SUMMARY:\n`;
      message += `• Total URLs processed: ${totalCount}\n`;
      message += `• ✅ Successfully imported: ${successCount} videos\n`;
      message += `• ❌ Failed imports: ${failedCount} videos\n\n`;
      
      if (successCount > 0) {
        message += `🎉 SUCCESS: ${successCount} new videos are now available on your site!\n\n`;
      }
      
      if (failedCount > 0) {
        message += `⚠️ FAILED IMPORTS:\n`;
        message += results.failed.map(item => `• ${item.url.slice(0, 50)}...: ${item.error}`).slice(0, 3).join('\n');
        if (results.failed.length > 3) {
          message += `\n... and ${results.failed.length - 3} more failed`;
        }
        message += `\n\n`;
      }
      
      // Show categorization results
      const categorized = results.success.filter(item => item.category && item.category !== 'uncategorized');
      if (categorized.length > 0) {
        message += `🏷️ AUTO-CATEGORIZED:\n`;
        message += categorized.map(item => `• ${item.title.slice(0, 30)}... → ${item.category}`).slice(0, 3).join('\n');
        if (categorized.length > 3) {
          message += `\n... and ${categorized.length - 3} more categorized`;
        }
      }
      
      alert(message);
      setBulkVideos('');
      
      // Refresh data to show new videos - wait a bit for database to sync
      setTimeout(async () => {
        await fetchVideos();
        await fetchDashboard();
        await fetchCategories();
      }, 1000);
      
    } catch (error) {
      console.error('Bulk import error:', error);
      let errorMessage = `❌ BULK IMPORT FAILED!\n\n`;
      
      if (error.response?.status === 401) {
        errorMessage += `Error: Authentication failed (Invalid token)\n\n`;
        errorMessage += `🔧 SOLUTION: Please login again to refresh your session.\n`;
        handleLogout();
      } else {
        const detail = error.response?.data?.detail || error.message || 'Unknown error occurred';
        errorMessage += `Error: ${detail}\n\n`;
        errorMessage += `🔧 Please check:\n`;
        errorMessage += `• Internet connection\n`;
        errorMessage += `• Video URLs are valid\n`;
        errorMessage += `• Server is running properly`;
      }
      
      alert(errorMessage);
    }
  };

  const deleteVideo = async (videoId) => {
    if (window.confirm('Are you sure you want to delete this video?')) {
      try {
        await axios.delete(`${API}/videos/${videoId}`, axiosConfig);
        alert('Video deleted successfully!');
        fetchVideos();
        fetchDashboard();
      } catch (error) {
        alert('Failed to delete video: ' + error.response?.data?.detail || error.message);
      }
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Card className="w-full max-w-md bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-center text-white">
              <span className="text-orange-400 font-black text-xl">Mega</span>videosee Admin
            </CardTitle>
            <CardDescription className="text-center text-gray-400">
              Sign in to access the admin panel
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="username" className="text-white">Username</Label>
                <Input
                  id="username"
                  type="text"
                  value={loginData.username}
                  onChange={(e) => setLoginData({...loginData, username: e.target.value})}
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                  data-testid="admin-username-input"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-white">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                  data-testid="admin-password-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-red-600 hover:bg-red-700"
                data-testid="admin-login-btn"
              >
                Sign In
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900" data-testid="admin-panel">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="text-2xl font-bold text-white">
                <span className="text-orange-400 font-black text-xl">Mega</videosee Admin
              </div>
              <Badge variant="secondary" className="bg-red-600/20 text-red-400 border-red-500/50">
                Management Panel
              </Badge>
            </div>
            <Button onClick={handleLogout} variant="outline" className="text-gray-300 border-gray-600">
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        <Tabs defaultValue="dashboard" className="space-y-6">
          <TabsList className="grid w-full grid-cols-8 bg-gray-800 border-gray-700">
            <TabsTrigger value="dashboard" className="text-white">{TR.dashboard}</TabsTrigger>
            <TabsTrigger value="videos" className="text-white">Videolar</TabsTrigger>
            <TabsTrigger value="categories" className="text-white">{TR.categories}</TabsTrigger>
            <TabsTrigger value="ads" className="text-white">{TR.ads}</TabsTrigger>
            <TabsTrigger value="import" className="text-white">{TR.bulkImport}</TabsTrigger>
            <TabsTrigger value="contacts" className="text-white">{TR.contacts}</TabsTrigger>
            <TabsTrigger value="help" className="text-white">{TR.help}</TabsTrigger>
            <TabsTrigger value="reports" className="text-white">{TR.reports}</TabsTrigger>
          </TabsList>

          {/* Dashboard */}
          <TabsContent value="dashboard">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Total Videos</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-red-400">{dashboard.total_videos || 0}</div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Categories</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-blue-400">{dashboard.total_categories || 0}</div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Active Ads</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-400">{dashboard.total_ads || 0}</div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Unique Visitors</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-purple-400">{analytics.unique_visitors || 0}</div>
                  <div className="text-xs text-gray-400">Last 30 days</div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Ad Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Total Impressions:</span>
                      <span className="text-white font-semibold">{dashboard.total_impressions?.toLocaleString() || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Total Clicks:</span>
                      <span className="text-white font-semibold">{dashboard.total_clicks?.toLocaleString() || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Visitor Analytics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Total Visits:</span>
                      <span className="text-white">{analytics.total_visits || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Video Views:</span>
                      <span className="text-white">{analytics.video_views || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Mobile Users:</span>
                      <span className="text-white">{analytics.mobile_percentage || 0}%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Videos Management */}
          <TabsContent value="videos">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">Video Management</h2>
              <Dialog>
                <DialogTrigger asChild>
                  <Button className="bg-red-600 hover:bg-red-700">Add New Video</Button>
                </DialogTrigger>
                <DialogContent className="bg-gray-800 border-gray-700 max-w-2xl">
                  <DialogHeader>
                    <DialogTitle className="text-white">Create New Video</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateVideo} className="space-y-4">
                    <div>
                      <Label className="text-white">Title</Label>
                      <Input
                        value={newVideo.title}
                        onChange={(e) => setNewVideo({...newVideo, title: e.target.value})}
                        className="bg-gray-700 border-gray-600 text-white"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-white">Embed URL</Label>
                      <div className="flex space-x-2">
                        <Input
                          value={newVideo.embed_url}
                          onChange={(e) => setNewVideo({...newVideo, embed_url: e.target.value})}
                          className="bg-gray-700 border-gray-600 text-white flex-1"
                          placeholder="https://player.vimeo.com/video/..."
                          required
                        />
                        <Button
                          type="button"
                          onClick={() => handleExtractVideoMetadata(newVideo.embed_url)}
                          className="bg-orange-600 hover:bg-orange-700 whitespace-nowrap"
                          disabled={!newVideo.embed_url || extractingVideoMetadata}
                        >
                          {extractingVideoMetadata ? 'Extracting...' : 'Auto-Fill'}
                        </Button>
                      </div>
                      {extractingVideoMetadata && (
                        <div className="text-orange-400 text-sm mt-1">
                          Extracting video metadata...
                        </div>
                      )}
                    </div>
                    <div>
                      <Label className="text-white">Description</Label>
                      <Textarea
                        value={newVideo.description}
                        onChange={(e) => setNewVideo({...newVideo, description: e.target.value})}
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-white">Thumbnail URL</Label>
                        <Input
                          value={newVideo.thumbnail_url}
                          onChange={(e) => setNewVideo({...newVideo, thumbnail_url: e.target.value})}
                          className="bg-gray-700 border-gray-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-white">Duration (seconds)</Label>
                        <Input
                          type="number"
                          value={newVideo.duration}
                          onChange={(e) => setNewVideo({...newVideo, duration: e.target.value})}
                          className="bg-gray-700 border-gray-600 text-white"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-white">Categories</Label>
                      <div className="max-h-32 overflow-y-auto bg-gray-700 border border-gray-600 rounded p-2 space-y-1">
                        {categories.map((category) => (
                          <label key={category.id} className="flex items-center space-x-2 text-white text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={newVideo.category_ids.includes(category.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setNewVideo({
                                    ...newVideo,
                                    category_ids: [...newVideo.category_ids, category.id]
                                  });
                                } else {
                                  setNewVideo({
                                    ...newVideo,
                                    category_ids: newVideo.category_ids.filter(id => id !== category.id)
                                  });
                                }
                              }}
                              className="rounded"
                            />
                            <span>{category.name}</span>
                          </label>
                        ))}
                        {categories.length === 0 && (
                          <div className="text-gray-400 text-sm">No categories available</div>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label className="text-white">Tags (comma-separated)</Label>
                      <Input
                        value={newVideo.tags}
                        onChange={(e) => setNewVideo({...newVideo, tags: e.target.value})}
                        className="bg-gray-700 border-gray-600 text-white"
                        placeholder="hd, premium, trending"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={newVideo.is_featured}
                        onChange={(e) => setNewVideo({...newVideo, is_featured: e.target.checked})}
                        className="rounded"
                      />
                      <Label className="text-white">Featured Video</Label>
                    </div>
                    <Button type="submit" className="w-full bg-red-600 hover:bg-red-700">
                      Create Video
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Bulk Operations Controls */}
            {Array.isArray(videos) && videos.length > 0 && (
              <div className="bg-gray-700 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <input
                      type="checkbox"
                      checked={selectedVideoIds.length === videos.length}
                      onChange={() => handleSelectAllVideos(videos, false)}
                      className="rounded"
                    />
                    <span className="text-white">
                      {selectedVideoIds.length === 0 
                        ? 'Select All' 
                        : `${selectedVideoIds.length} video${selectedVideoIds.length > 1 ? 's' : ''} selected`
                      }
                    </span>
                  </div>
                  {selectedVideoIds.length > 0 && (
                    <Button
                      onClick={() => handleBulkDeleteVideos(selectedVideoIds, false)}
                      variant="destructive"
                      disabled={bulkDeleteInProgress}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      {bulkDeleteInProgress ? 'Deleting...' : `Delete ${selectedVideoIds.length} Selected`}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Enhanced Video List with Thumbnails */}
            <div className="grid gap-4">
              {Array.isArray(videos) && videos.map((video) => (
                <Card key={video.id} className="bg-gray-800 border-gray-700">
                  <CardContent className="p-4">
                    <div className="flex items-start space-x-4">
                      {/* Checkbox for bulk selection */}
                      <input
                        type="checkbox"
                        checked={selectedVideoIds.includes(video.id)}
                        onChange={() => handleVideoSelection(video.id, false)}
                        className="rounded mt-2"
                      />
                      
                      {/* Thumbnail */}
                      <div className="flex-shrink-0">
                        <img
                          src={video.thumbnail_url || 'https://picsum.photos/120/68?random=' + Math.random()}
                          alt={video.title}
                          className="w-24 h-14 object-cover rounded bg-gray-700"
                          onError={(e) => {
                            e.target.src = 'https://picsum.photos/120/68?random=' + Math.random();
                          }}
                        />
                        <div className="text-xs text-center text-gray-400 mt-1">
                          {formatDuration(video.duration)}
                        </div>
                      </div>
                      
                      {/* Video Information */}
                      <div className="flex-1">
                        <h3 className="text-white font-semibold mb-2 line-clamp-2">{video.title}</h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-400 mb-2">
                          <span>👁 {video.view_count?.toLocaleString() || '0'} views</span>
                          <span>⭐ {video.rating || 'N/A'}</span>
                          <span>🕒 {formatDuration(video.duration)}</span>
                          {video.is_featured && <Badge className="bg-red-600">Featured</Badge>}
                          {video.is_latest_upload && <Badge className="bg-green-600">Latest</Badge>}
                        </div>
                        {video.description && (
                          <p className="text-gray-400 text-sm mb-2 line-clamp-2">{video.description}</p>
                        )}
                        <div className="flex flex-wrap gap-1">
                          {video.tags && video.tags.slice(0, 5).map((tag, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                          {video.tags && video.tags.length > 5 && (
                            <Badge variant="outline" className="text-xs">
                              +{video.tags.length - 5} more
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex flex-col space-y-2 ml-4">
                        <Button
                          onClick={() => window.open(`/video/${video.id}`, '_blank')}
                          variant="outline"
                          size="sm"
                          className="text-blue-400 border-blue-400 hover:bg-blue-400 hover:text-white"
                        >
                          View
                        </Button>
                        <Button
                          onClick={() => handleDeleteVideo(video.id)}
                          variant="destructive"
                          size="sm"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Categories Management */}
          <TabsContent value="categories">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">Category Management</h2>
              <Dialog>
                <DialogTrigger asChild>
                  <Button className="bg-red-600 hover:bg-red-700">Add New Category</Button>
                </DialogTrigger>
                <DialogContent className="bg-gray-800 border-gray-700">
                  <DialogHeader>
                    <DialogTitle className="text-white">Create New Category</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateCategory} className="space-y-4">
                    <div>
                      <Label className="text-white">Name</Label>
                      <Input
                        value={newCategory.name}
                        onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                        className="bg-gray-700 border-gray-600 text-white"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-white">Description</Label>
                      <Textarea
                        value={newCategory.description}
                        onChange={(e) => setNewCategory({...newCategory, description: e.target.value})}
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-white">Category Image</Label>
                      <div className="space-y-3">
                        <Input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const imageUrl = await handleCategoryImageUpload(file);
                              if (imageUrl) {
                                setNewCategory({...newCategory, image_url: imageUrl});
                              }
                            }
                          }}
                          className="bg-gray-700 border-gray-600 text-white file:bg-orange-600 file:text-white file:border-0 file:rounded file:px-4 file:py-2 file:mr-4"
                          disabled={uploadingCategoryImage}
                        />
                        {uploadingCategoryImage && (
                          <div className="text-orange-400 text-sm">Uploading image...</div>
                        )}
                        {newCategory.image_url && (
                          <div className="relative">
                            <img 
                              src={`${BACKEND_URL}${newCategory.image_url}`} 
                              alt="Category preview" 
                              className="w-full h-32 object-cover rounded border border-gray-600"
                            />
                            <Button
                              type="button"
                              size="sm"
                              variant="destructive"
                              className="absolute top-2 right-2"
                              onClick={() => setNewCategory({...newCategory, image_url: ''})}
                            >
                              Remove
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                    <Button type="submit" className="w-full bg-red-600 hover:bg-red-700">
                      Create Category
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Bulk Operations for Categories */}
            {Array.isArray(categories) && categories.length > 0 && (
              <div className="bg-gray-700 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <input
                      type="checkbox"
                      checked={selectedCategoryIds.length === categories.filter(cat => !isProtectedCategory(cat.name)).length}
                      onChange={handleSelectAllCategories}
                      className="rounded"
                    />
                    <span className="text-white">
                      {selectedCategoryIds.length === 0 
                        ? 'Select All (Non-Protected)' 
                        : `${selectedCategoryIds.length} categor${selectedCategoryIds.length > 1 ? 'ies' : 'y'} selected`
                      }
                    </span>
                  </div>
                  {selectedCategoryIds.length > 0 && (
                    <Button
                      onClick={handleBulkDeleteCategories}
                      variant="destructive"
                      disabled={bulkCategoryDeleteInProgress}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      {bulkCategoryDeleteInProgress ? 'Deleting...' : `Delete ${selectedCategoryIds.length} Selected`}
                    </Button>
                  )}
                </div>
              </div>
            )}

            <div className="grid gap-4">
              {Array.isArray(sortedCategories) && sortedCategories.map((category) => (
                <Card key={category.id} className="bg-gray-800 border-gray-700 hover:bg-gray-700 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start space-x-4">
                      {/* Checkbox for bulk selection (only for non-protected) */}
                      {!isProtectedCategory(category.name) && (
                        <input
                          type="checkbox"
                          checked={selectedCategoryIds.includes(category.id)}
                          onChange={() => handleCategorySelection(category.id)}
                          className="rounded mt-2"
                        />
                      )}
                      
                      {/* Protected category spacer */}
                      {isProtectedCategory(category.name) && (
                        <div className="w-4 mt-2"></div>
                      )}
                      
                      {/* Category Image */}
                      <div className="flex-shrink-0">
                        <img
                          src={category.image_url ? `${BACKEND_URL}${category.image_url}` : 'https://picsum.photos/120/68?random=' + Math.random()}
                          alt={category.name}
                          className="w-24 h-14 object-cover rounded bg-gray-700 border border-gray-600"
                          onError={(e) => {
                            e.target.src = 'https://picsum.photos/120/68?random=' + Math.random();
                          }}
                        />
                      </div>
                      
                      {/* Category Information */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <CardTitle className="text-white">
                            {category.name}
                            {isProtectedCategory(category.name) && (
                              <Badge className="ml-2 bg-blue-600/20 text-blue-400 border-blue-500/50">
                                Protected
                              </Badge>
                            )}
                          </CardTitle>
                          <Badge className="bg-orange-600/20 text-orange-400 border-orange-500/50">
                            {categoryVideoCount[category.id] || 0} videos
                          </Badge>
                        </div>
                        {category.description && (
                          <CardDescription className="text-gray-400 mb-2">{category.description}</CardDescription>
                        )}
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex flex-col space-y-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-orange-400 border-orange-400 hover:bg-orange-400 hover:text-white"
                          onClick={() => handleCategoryClick(category.id)}
                        >
                          View Videos
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-blue-400 border-blue-400 hover:bg-blue-400 hover:text-white"
                          onClick={() => handleEditCategory(category)}
                        >
                          Edit
                        </Button>
                        {!isProtectedCategory(category.name) && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteCategory(category)}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Category Videos Modal */}
            {showCategoryVideos && selectedCategoryId && (
              <Dialog open={showCategoryVideos} onOpenChange={setShowCategoryVideos}>
                <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto bg-gray-800 border-gray-700">
                  <DialogHeader>
                    <DialogTitle className="text-white">
                      Videos in "{categories.find(c => c.id === selectedCategoryId)?.name}" Category
                    </DialogTitle>
                  </DialogHeader>
                  <div className="mt-4">
                    {categoryVideos.length > 0 ? (
                      <>
                        {/* Bulk Operations Controls for Category */}
                        <div className="bg-gray-600 rounded-lg p-4 mb-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <input
                                type="checkbox"
                                checked={selectedCategoryVideoIds.length === categoryVideos.length}
                                onChange={() => handleSelectAllVideos(categoryVideos, true)}
                                className="rounded"
                              />
                              <span className="text-white">
                                {selectedCategoryVideoIds.length === 0 
                                  ? 'Select All in Category' 
                                  : `${selectedCategoryVideoIds.length} video${selectedCategoryVideoIds.length > 1 ? 's' : ''} selected`
                                }
                              </span>
                            </div>
                            {selectedCategoryVideoIds.length > 0 && (
                              <Button
                                onClick={() => handleBulkDeleteVideos(selectedCategoryVideoIds, true)}
                                variant="destructive"
                                disabled={bulkDeleteInProgress}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                {bulkDeleteInProgress ? 'Deleting...' : `Delete ${selectedCategoryVideoIds.length} Selected`}
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Enhanced Category Video List */}
                        <div className="grid gap-4">
                          {categoryVideos.map((video) => (
                            <Card key={video.id} className="bg-gray-700 border-gray-600">
                              <CardContent className="p-4">
                                <div className="flex items-start space-x-4">
                                  {/* Checkbox for bulk selection */}
                                  <input
                                    type="checkbox"
                                    checked={selectedCategoryVideoIds.includes(video.id)}
                                    onChange={() => handleVideoSelection(video.id, true)}
                                    className="rounded mt-2"
                                  />
                                  
                                  {/* Thumbnail */}
                                  <div className="flex-shrink-0">
                                    <img
                                      src={video.thumbnail_url || 'https://picsum.photos/120/68?random=' + Math.random()}
                                      alt={video.title}
                                      className="w-24 h-14 object-cover rounded bg-gray-600"
                                      onError={(e) => {
                                        e.target.src = 'https://picsum.photos/120/68?random=' + Math.random();
                                      }}
                                    />
                                    <div className="text-xs text-center text-gray-400 mt-1">
                                      {formatDuration(video.duration)}
                                    </div>
                                  </div>
                                  
                                  {/* Video Information */}
                                  <div className="flex-1">
                                    <h3 className="text-white font-semibold mb-2 line-clamp-2">{video.title}</h3>
                                    {video.description && (
                                      <p className="text-gray-400 text-sm mb-2 line-clamp-2">{video.description}</p>
                                    )}
                                    <div className="flex items-center gap-4 text-sm text-gray-400 mb-2">
                                      <span>👁 {video.view_count?.toLocaleString() || 0}</span>
                                      <span>⭐ {video.rating || 'N/A'}</span>
                                      <span>🕒 {formatDuration(video.duration)}</span>
                                    </div>
                                    {video.tags && video.tags.length > 0 && (
                                      <div className="flex flex-wrap gap-1">
                                        {video.tags.slice(0, 5).map((tag, index) => (
                                          <Badge key={index} variant="secondary" className="text-xs">
                                            {tag}
                                          </Badge>
                                        ))}
                                        {video.tags.length > 5 && (
                                          <Badge variant="secondary" className="text-xs">
                                            +{video.tags.length - 5} more
                                          </Badge>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Action Buttons */}
                                  <div className="flex flex-col gap-2 ml-4">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-blue-400 border-blue-400 hover:bg-blue-400 hover:text-white"
                                      onClick={() => window.open(`/video/${video.id}`, '_blank')}
                                    >
                                      View
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      onClick={() => handleDeleteVideo(video.id)}
                                    >
                                      Delete
                                    </Button>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-8 text-gray-400">
                        No videos found in this category
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            )}

            {/* Edit Category Dialog */}
            {showEditCategoryDialog && editingCategory && (
              <Dialog open={showEditCategoryDialog} onOpenChange={setShowEditCategoryDialog}>
                <DialogContent className="max-w-md bg-gray-800 border-gray-700">
                  <DialogHeader>
                    <DialogTitle className="text-white">
                      Edit Category: {editingCategory.name}
                      {isProtectedCategory(editingCategory.name) && (
                        <Badge className="ml-2 bg-blue-600/20 text-blue-400 border-blue-500/50">
                          Protected
                        </Badge>
                      )}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleUpdateCategory} className="space-y-4">
                    <div>
                      <label className="text-white text-sm font-medium mb-2 block">
                        Category Name
                      </label>
                      <Input
                        value={editCategoryForm.name}
                        onChange={(e) => setEditCategoryForm({...editCategoryForm, name: e.target.value})}
                        className="bg-gray-700 border-gray-600 text-white"
                        placeholder="Enter category name"
                        required
                        disabled={isProtectedCategory(editingCategory.name)}
                      />
                    </div>
                    <div>
                      <label className="text-white text-sm font-medium mb-2 block">
                        Description
                      </label>
                      <Input
                        value={editCategoryForm.description}
                        onChange={(e) => setEditCategoryForm({...editCategoryForm, description: e.target.value})}
                        className="bg-gray-700 border-gray-600 text-white"
                        placeholder="Enter category description"
                      />
                    </div>
                    <div>
                      <label className="text-white text-sm font-medium mb-2 block">
                        Category Image URL
                      </label>
                      <Input
                        value={editCategoryForm.image_url}
                        onChange={(e) => setEditCategoryForm({...editCategoryForm, image_url: e.target.value, image_file: null})}
                        className="bg-gray-700 border-gray-600 text-white"
                        placeholder="Enter image URL or upload file below"
                      />
                    </div>
                    <div>
                      <label className="text-white text-sm font-medium mb-2 block">
                        Or Upload Image from Computer
                      </label>
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file && validateImageFile(file)) {
                            setEditCategoryForm({
                              ...editCategoryForm, 
                              image_file: file,
                              image_url: '' // Clear URL when file is selected
                            });
                          } else if (file) {
                            // Clear the file input if validation failed
                            e.target.value = '';
                          }
                        }}
                        className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm w-full file:mr-4 file:py-1 file:px-2 file:rounded file:border-0 file:text-sm file:font-medium file:bg-orange-600 file:text-white hover:file:bg-orange-700"
                      />
                      <p className="text-gray-400 text-xs mt-1">
                        Supports JPG and PNG formats. Recommended: 400x226 pixels. Images will be automatically resized.
                      </p>
                    </div>
                    {(editCategoryForm.image_url || editCategoryForm.image_file) && (
                      <div>
                        <label className="text-white text-sm font-medium mb-2 block">
                          Image Preview (400x226 target size)
                        </label>
                        <img
                          src={
                            editCategoryForm.image_file 
                              ? URL.createObjectURL(editCategoryForm.image_file)
                              : editCategoryForm.image_url.startsWith('/') 
                                ? `${BACKEND_URL}${editCategoryForm.image_url}` 
                                : editCategoryForm.image_url
                          }
                          alt="Category preview"
                          className="w-full max-w-[400px] h-auto object-cover rounded border border-gray-600"
                          style={{aspectRatio: '400/226'}}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    <div className="flex justify-end space-x-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowEditCategoryDialog(false)}
                        className="text-gray-400 border-gray-600 hover:bg-gray-700"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        className="bg-orange-600 hover:bg-orange-700 text-white"
                      >
                        Update Category
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </TabsContent>

          {/* Ads Management - Placement Based */}
          <TabsContent value="ads">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">Ad Placement Management</h2>
            </div>

            {/* Ad Placement Areas */}
            <div className="space-y-4">
              <p className="text-gray-400 text-sm">
                Manage ads for each placement area on your site. Each area can only have one active ad at a time.
              </p>
              
              {adPlacements.map((placement) => (
                <div key={placement.id} className="bg-gray-700 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-white font-semibold text-lg">{placement.name}</h3>
                      <p className="text-gray-400 text-sm">{placement.description}</p>
                      <div className="flex items-center space-x-4 mt-2">
                        <Badge variant="outline" className="text-gray-300">
                          {placement.default_size || 'Flexible'}
                        </Badge>
                        <Badge variant={placement.current_ad ? 'default' : 'secondary'}>
                          {placement.current_ad ? 'Active Ad' : 'No Ad'}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-white border-gray-600 hover:bg-gray-600"
                        onClick={() => handlePreviewPlacement(placement.id)}
                      >
                        Preview
                      </Button>
                      <Button 
                        size="sm" 
                        className="bg-orange-600 hover:bg-orange-700"
                        onClick={() => handleEditPlacement(placement)}
                      >
                        {placement.current_ad ? 'Edit Ad' : 'Add Ad'}
                      </Button>
                    </div>
                  </div>
                  
                  {placement.current_ad && (
                    <div className="bg-gray-600 rounded p-3 mt-3">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-white text-sm font-medium">
                            {placement.current_ad.name || placement.current_ad.title || 'Active Ad'}
                          </p>
                          <p className="text-gray-300 text-xs">
                            Created: {new Date(placement.current_ad.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Badge variant="success" className="bg-green-600">Active</Badge>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Placement Ad Edit Dialog */}
            <Dialog open={showPlacementDialog} onOpenChange={setShowPlacementDialog}>
              <DialogContent className="bg-gray-800 border-gray-700 max-w-4xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-white">
                    {editingPlacement?.current_ad ? 'Edit Ad for' : 'Add Ad for'} {editingPlacement?.name}
                  </DialogTitle>
                </DialogHeader>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Ad Form */}
                  <div>
                    <form onSubmit={handleUpdatePlacement} className="space-y-4">
                      {/* Different inputs based on ad type */}
                      {editingPlacement?.ad_types?.includes('vast') ? (
                        <div>
                          <Label className="text-white">VAST URL</Label>
                          <Input
                            value={placementAdForm.vast_url}
                            onChange={(e) => setPlacementAdForm({...placementAdForm, vast_url: e.target.value})}
                            className="bg-gray-700 border-gray-600 text-white"
                            placeholder="https://tsyndicate.com/do2/5c22dbed79074953b548acb8d2cb427b/vast"
                            required
                          />
                          <p className="text-xs text-gray-400 mt-1">
                            Enter the VAST XML URL for video ads. No iframe code needed.
                          </p>
                        </div>
                      ) : editingPlacement?.ad_types?.includes('native') ? (
                        <div>
                          <Label className="text-white">Native Ad Script</Label>
                          <Textarea
                            value={placementAdForm.native_code}
                            onChange={(e) => setPlacementAdForm({...placementAdForm, native_code: e.target.value})}
                            className="bg-gray-700 border-gray-600 text-white h-48"
                            placeholder={`<div id="ts_ad_native_1oidi"></div>
<script src="//cdn.tsyndicate.com/sdk/v1/n.js"></script>
<script>
  NativeAd({
    element_id: "ts_ad_native_1oidi",
    spot: "your_spot_id",
    type: "label-under",
    cols: 4,
    rows: 1,
    title: "Suggested for you"
  });
</script>`}
                            required
                          />
                          <p className="text-xs text-gray-400 mt-1">
                            Paste the complete native ad script including &lt;div&gt; and &lt;script&gt; tags.
                          </p>
                        </div>
                      ) : (
                        <div>
                          <Label className="text-white">Banner Iframe Code</Label>
                          <Textarea
                            value={placementAdForm.iframe_code}
                            onChange={(e) => setPlacementAdForm({...placementAdForm, iframe_code: e.target.value})}
                            className="bg-gray-700 border-gray-600 text-white h-32"
                            placeholder="<iframe src='https://example.com/ad' width='300' height='250'></iframe>"
                            required
                          />
                          <p className="text-xs text-gray-400 mt-1">
                            Paste the iframe code for banner ads. Recommended size: {editingPlacement?.default_size}
                          </p>
                        </div>
                      )}
                      
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="ad_active"
                          checked={placementAdForm.is_active}
                          onChange={(e) => setPlacementAdForm({...placementAdForm, is_active: e.target.checked})}
                          className="rounded bg-gray-700 border-gray-600"
                        />
                        <Label htmlFor="ad_active" className="text-white text-sm">
                          Ad is active
                        </Label>
                      </div>
                      
                      <div className="flex space-x-2">
                        <Button type="submit" className="bg-red-600 hover:bg-red-700 flex-1">
                          {editingPlacement?.current_ad ? 'Update Ad' : 'Create Ad'}
                        </Button>
                        <Button 
                          type="button" 
                          variant="outline"
                          className="border-gray-600 text-white hover:bg-gray-700"
                          onClick={() => handlePreviewPlacement(editingPlacement?.id)}
                        >
                          Refresh Preview
                        </Button>
                      </div>
                    </form>
                  </div>
                  
                  {/* Preview */}
                  <div>
                    <Label className="text-white">Preview</Label>
                    <div className="bg-gray-900 border border-gray-600 rounded p-4 min-h-[200px] mt-2">
                      {placementPreview ? (
                        <div dangerouslySetInnerHTML={{ __html: placementPreview }} />
                      ) : (
                        <div className="text-center text-gray-400 py-8">
                          Click "Refresh Preview" to see how this ad will appear
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Website Video Scraping */}
          <TabsContent value="import">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Website Video Scraping</CardTitle>
                <CardDescription className="text-gray-400">
                  Scrape all videos from a website category page (e.g., xhamster.com/categories/amateur)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label className="text-white">Website Category URL</Label>
                    <Input
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(e.target.value)}
                      placeholder="https://xhamster.com/categories/amateur"
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-white">Assign to Category</Label>
                    <Select value={selectedCategoryForScraping} onValueChange={setSelectedCategoryForScraping}>
                      <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                        <SelectValue placeholder="Select category for all scraped videos" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-white">Max Videos to Scrape (0 = No Limit)</Label>
                    <Input
                      type="number"
                      value={maxVideosToScrape}
                      onChange={(e) => setMaxVideosToScrape(parseInt(e.target.value) || 0)}
                      min="0"
                      className="bg-gray-700 border-gray-600 text-white"
                      placeholder="0 for unlimited, or enter a number"
                    />
                  </div>
                  
                  <Button
                    onClick={handleWebsiteScraping}
                    className="w-full bg-orange-600 hover:bg-orange-700"
                    disabled={!websiteUrl.trim() || !selectedCategoryForScraping || scrapingInProgress}
                  >
                    {scrapingInProgress ? 'Scraping Videos...' : 'Scrape Website Videos'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contact Messages */}
          <TabsContent value="contacts">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-white">{TR.contacts}</h2>
            </div>

            <div className="grid gap-4">
              {contactMessages.length > 0 ? (
                contactMessages.map((message) => (
                  <Card key={message.id || message._id} className="bg-gray-800 border-gray-700">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-white font-semibold">{message.subject}</h3>
                        <span className="text-gray-400 text-sm">
                          {new Date(message.timestamp).toLocaleDateString('tr-TR')}
                        </span>
                      </div>
                      <div className="text-gray-400 text-sm mb-2">
                        <span className="font-medium">{TR.sender}:</span> {message.name} ({message.email})
                      </div>
                      <p className="text-gray-300">{message.message}</p>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className="text-center py-12">
                  <div className="text-4xl mb-2">📧</div>
                  <div className="text-gray-400">{TR.noMessages}</div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Help Requests */}
          <TabsContent value="help">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-white">{TR.help}</h2>
            </div>

            <div className="grid gap-4">
              {helpRequests.length > 0 ? (
                helpRequests.map((request) => (
                  <Card key={request.id || request._id} className="bg-gray-800 border-gray-700">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-white font-semibold">{request.subject}</h3>
                        <span className="text-gray-400 text-sm">
                          {new Date(request.timestamp).toLocaleDateString('tr-TR')}
                        </span>
                      </div>
                      <div className="text-gray-400 text-sm mb-2">
                        <span className="font-medium">{TR.sender}:</span> {request.name} ({request.email})
                      </div>
                      <div className="text-gray-400 text-sm mb-2">
                        <span className="font-medium">Kategori:</span> {request.category}
                      </div>
                      <p className="text-gray-300">{request.message}</p>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className="text-center py-12">
                  <div className="text-4xl mb-2">🎯</div>
                  <div className="text-gray-400">Henüz yardım talebi yok</div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Content Reports */}
          <TabsContent value="reports">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-white">{TR.reports}</h2>
            </div>

            <div className="grid gap-4">
              {contentReports.length > 0 ? (
                contentReports.map((report) => (
                  <Card key={report.id || report._id} className="bg-gray-800 border-gray-700">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-white font-semibold">{report.report_type.replace('_', ' ').toUpperCase()}</h3>
                        <span className="text-gray-400 text-sm">
                          {new Date(report.timestamp).toLocaleDateString('tr-TR')}
                        </span>
                      </div>
                      {(report.reporter_name || report.reporter_email) && (
                        <div className="text-gray-400 text-sm mb-2">
                          <span className="font-medium">Rapor Eden:</span> {report.reporter_name || 'Anonim'} 
                          {report.reporter_email && ` (${report.reporter_email})`}
                        </div>
                      )}
                      {report.content_url && (
                        <div className="text-gray-400 text-sm mb-2">
                          <span className="font-medium">İçerik URL:</span> 
                          <a href={report.content_url} target="_blank" rel="noopener noreferrer" 
                             className="text-orange-400 hover:text-orange-300 break-all ml-1">
                            {report.content_url}
                          </a>
                        </div>
                      )}
                      <p className="text-gray-300 mb-2">{report.description}</p>
                      {report.additional_info && (
                        <div className="text-gray-400 text-sm">
                          <span className="font-medium">Ek Bilgi:</span> {report.additional_info}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className="text-center py-12">
                  <div className="text-4xl mb-2">🛡️</div>
                  <div className="text-gray-400">Henüz şikayet yok</div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminPanel;